import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';
import { BooleanWindowSizeType, WindowSizeState, WindowSizeType } from './types';
import { detectDesktopLayout, detectIos, detectMobile } from './utils';

/**Создает стор. Для получения значений используем useWindowSize как и раньше.
 * ОБЯЗАТЕЛЬНО добавить подписку на ресайз применив useInitWindowSizeStore
 */
export const useWindowSizeStore = create<WindowSizeState>((set) => ({
  width: window.innerWidth,
  height: window.innerHeight,
  is320: window.innerWidth >= 320,
  is360: window.innerWidth >= 360,
  is375: window.innerWidth >= 375,
  is412: window.innerWidth >= 412,
  is568: window.innerWidth >= 568,
  is640: window.innerWidth >= 640,
  is768: window.innerWidth >= 768,
  is1024: window.innerWidth >= 1024,
  is1170: window.innerWidth >= 1170,
  is1280: window.innerWidth >= 1280,
  is1440: window.innerWidth >= 1440,
  is1920: window.innerWidth >= 1920,
  is2560: window.innerWidth >= 2560,
  isIos: detectIos(),
  isMobile: detectMobile(),
  isMobileLayout: !detectDesktopLayout(),
  isDesktopLayout: detectDesktopLayout(),
  isLandscape: window.innerWidth > window.innerHeight,
  updateWindowSize: () =>
    set({
      width: window.innerWidth,
      height: window.innerHeight,
      is320: window.innerWidth >= 320,
      is360: window.innerWidth >= 360,
      is375: window.innerWidth >= 375,
      is412: window.innerWidth >= 412,
      is568: window.innerWidth >= 568,
      is640: window.innerWidth >= 640,
      is768: window.innerWidth >= 768,
      is1024: window.innerWidth >= 1024,
      is1170: window.innerWidth >= 1170,
      is1280: window.innerWidth >= 1280,
      is1440: window.innerWidth >= 1440,
      is1920: window.innerWidth >= 1920,
      is2560: window.innerWidth >= 2560,
      isIos: detectIos(),
      isMobile: detectMobile(),
      isMobileLayout: !detectDesktopLayout(),
      isDesktopLayout: detectDesktopLayout(),
      isLandscape: window.innerWidth > window.innerHeight
    })
}));

/**Добавляет подписку на ресайз для useWindowSizeStore.
 *
 * Поместить в DoSomethingOnEveryPage или роутер
 */
export const useInitWindowSizeStore = () => {
  const updateWindowSize = useWindowSizeStore((state) => state.updateWindowSize);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function handleResize() {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        updateWindowSize();
        timeoutRef.current = null;
      }, 100);
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
};

// Перегрузка 1: без аргументов → BooleanWindowSizeType
export function useWindowSize(): BooleanWindowSizeType;

// Перегрузка 2: с аргументами → Pick<WindowSizeType, T>
export function useWindowSize<T extends keyof WindowSizeType>(
  ...args: T[]
): Pick<WindowSizeType, T>;

/**Аналог хука useWindowSize.
 * Позволяет получить текущую ширину и высоту окна, а также булевы значения breakpoints.
 * Определяет мобильное устройство и устройство от Apple, а также горизонтальное положение устройства.
 * В дополнении две переменные для выбора мобильной и декстопной верстки
 *
 * Два варианта использования:
 * 1) Если передаем аргументы в хук. Происходит подписка только на эти значения.
 * Получить width и height мы можем только в этом варианте
 * 2) Если ничего не передаем - происходит подписка на обьект со всеми булевыми значениями.
 * @return
 * - width
 * - height
 * - is320
 * - is360
 * - is375
 * - is412
 * - is568
 * - is640
 * - is768
 * - is1024
 * - is1170
 * - is1280
 * - is1440
 * - is1920
 * - is2560
 * - isIos - Устройство от Apple
 * - isMobile - Мобильное устройство
 * - isMobileLayout - Мобильная верстка если юзер агент мобилка или дэкстоп ширина экрана меньше 768px
 * - isDesktopLayout - Декстопная верcтка если юзер агент декстоп или мобильное устройство ширина экрана больше 1024px
 * - isLandscape - Устройство находится в горизонтальном положении
 */
export function useWindowSize<T extends keyof WindowSizeType>(
  ...args: T[]
): BooleanWindowSizeType | Pick<WindowSizeType, T> {
  if (args.length === 0) {
    return useWindowSizeStore(useShallow(({ width, height, updateWindowSize, ...rest }) => rest));
  }
  return useWindowSizeStore(
    useShallow((state) =>
      args.reduce(
        (acc, key) => {
          acc[key] = state[key];
          return acc;
        },
        {} as Pick<WindowSizeType, T>
      )
    )
  );
}
